// First-party, cookieless analytics client.
//
// Captures anonymous visitor behaviour (page views, clicks, time on page,
// conversions) and ships it in small batches to the `track` edge function,
// which derives country/device server-side and stores rows in
// `analytics_events`. No third-party scripts, no tracking cookies — just a
// random visitor id in localStorage and a session id in sessionStorage.
//
// This runs independently of the marketing/consent stack in tracking.ts.

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
const ENDPOINT = `${SUPABASE_URL}/functions/v1/track`;

const VISITOR_KEY = "dc_vid_v1";
const SESSION_KEY = "dc_sid_v1";
const SESSION_TS_KEY = "dc_sid_ts_v1";
const SESSION_UTM_KEY = "dc_sutm_v1";
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 min of inactivity ends a session

const ANALYTICS_UTM_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_id",
] as const;

/**
 * Acquisition UTMs for the CURRENT session.
 *
 * Unlike the first-touch attribution in tracking.ts (which is kept forever for
 * lead/contact-form crediting), the dashboard needs the UTM of the source that
 * brought the visitor in *this time*. So we read the UTM params off the current
 * URL whenever they are present and remember them at session scope. If the URL
 * has no UTMs (e.g. an internal SPA navigation) we reuse the session value.
 * This way re-firing a link with a new utm_medium is reflected immediately,
 * even for a returning visitor.
 */
function getSessionUtm(): Record<string, string> {
  if (typeof window === "undefined") return {};
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl: Record<string, string> = {};
    for (const key of ANALYTICS_UTM_PARAMS) {
      const val = params.get(key);
      if (val) fromUrl[key] = val.slice(0, 120);
    }
    if (Object.keys(fromUrl).length > 0) {
      sessionStorage.setItem(SESSION_UTM_KEY, JSON.stringify(fromUrl));
      return fromUrl;
    }
    return JSON.parse(sessionStorage.getItem(SESSION_UTM_KEY) || "{}") as Record<
      string,
      string
    >;
  } catch {
    return {};
  }
}

type EventType = "pageview" | "click" | "conversion" | "engagement";

interface QueuedEvent {
  type: EventType;
  name?: string;
  path?: string;
  referrerHost?: string;
  isEntry?: boolean;
  durationMs?: number;
}

const isBrowser = () => typeof window !== "undefined";
const isPrerender = () => isBrowser() && (window as { __PRERENDER__?: boolean }).__PRERENDER__ === true;
// The admin/dashboard is internal — never record its traffic in site analytics.
const isAdminPath = () => isBrowser() && window.location.pathname.startsWith("/admin");

function uid(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

export function getVisitorId(): string {
  try {
    let v = localStorage.getItem(VISITOR_KEY);
    if (!v) {
      v = uid();
      localStorage.setItem(VISITOR_KEY, v);
    }
    return v;
  } catch {
    return "anon";
  }
}

export function getSessionId(): string {
  try {
    const now = Date.now();
    const last = Number(sessionStorage.getItem(SESSION_TS_KEY) || 0);
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid || !last || now - last > SESSION_TIMEOUT) {
      sid = uid();
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    sessionStorage.setItem(SESSION_TS_KEY, String(now));
    return sid;
  } catch {
    return "session";
  }
}

function referrerHost(): string | undefined {
  try {
    if (!document.referrer) return undefined;
    const url = new URL(document.referrer);
    if (url.host === window.location.host) return undefined;
    return url.host;
  } catch {
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// Batching
// ---------------------------------------------------------------------------
let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let started = false;

function buildPayload(events: QueuedEvent[]) {
  const utm = getSessionUtm();
  return {
    visitorId: getVisitorId(),
    sessionId: getSessionId(),
    locale: document.documentElement.lang || undefined,
    utm: {
      utm_source: utm.utm_source,
      utm_medium: utm.utm_medium,
      utm_campaign: utm.utm_campaign,
      utm_id: utm.utm_id,
    },
    events,
  };
}

function flush(useBeacon = false) {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0) return;
  const events = queue;
  queue = [];
  const payload = JSON.stringify(buildPayload(events));

  try {
    if (useBeacon && navigator.sendBeacon) {
      // sendBeacon survives page unload. The function is public (no auth needed).
      navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: "application/json" }));
      return;
    }
  } catch {
    /* fall through to fetch */
  }

  void fetch(ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json", apikey: ANON_KEY },
    body: payload,
    keepalive: true,
  }).catch(() => {
    /* analytics must never break the app */
  });
}

function enqueue(event: QueuedEvent) {
  if (!isBrowser() || isPrerender()) return;
  queue.push(event);
  if (queue.length >= 20) {
    flush();
    return;
  }
  if (!flushTimer) flushTimer = setTimeout(() => flush(), 4000);
}

// ---------------------------------------------------------------------------
// Page-view + engagement tracking
// ---------------------------------------------------------------------------
let currentPath: string | null = null;
let pageEnterTs = 0;
let pageviewCount = 0;

function sendEngagement() {
  if (!currentPath || !pageEnterTs) return;
  const durationMs = Date.now() - pageEnterTs;
  if (durationMs < 500) return; // ignore instant bounces / double fires
  enqueue({ type: "engagement", path: currentPath, durationMs });
}

export function trackPageview(path: string) {
  if (!isBrowser() || isPrerender()) return;
  if (path === currentPath) return;
  // Close out the previous page's engagement time.
  sendEngagement();

  // Admin/dashboard pages are internal — don't count them. Reset state so no
  // subsequent clicks or engagement get attributed to the previous public page.
  if (path.startsWith("/admin")) {
    currentPath = null;
    pageEnterTs = 0;
    return;
  }

  currentPath = path;
  pageEnterTs = Date.now();
  pageviewCount += 1;
  enqueue({
    type: "pageview",
    path,
    referrerHost: pageviewCount === 1 ? referrerHost() : undefined,
    isEntry: pageviewCount === 1,
  });
}

export function trackAnalyticsConversion(name: string, detail?: string) {
  if (isAdminPath()) return;
  enqueue({
    type: "conversion",
    name: detail ? `${name}:${detail}` : name,
    path: currentPath ?? undefined,
  });
  flush(); // conversions are important — send right away
}

// ---------------------------------------------------------------------------
// Click tracking (delegated, captures the most meaningful label)
// ---------------------------------------------------------------------------
function labelForElement(el: Element): string | null {
  const target = (el.closest("[data-track]") ||
    el.closest("a,button,[role='button']")) as HTMLElement | null;
  if (!target) return null;

  const explicit = target.getAttribute("data-track");
  if (explicit) return explicit.slice(0, 100);

  const aria = target.getAttribute("aria-label");
  if (aria) return aria.trim().slice(0, 100);

  const text = (target.textContent || "").replace(/\s+/g, " ").trim();
  if (text) return text.slice(0, 100);

  if (target.tagName === "A") {
    const href = target.getAttribute("href");
    if (href) return href.slice(0, 100);
  }
  return null;
}

function onClick(e: MouseEvent) {
  if (isAdminPath()) return;
  const el = e.target as Element | null;
  if (!el) return;
  const label = labelForElement(el);
  if (!label) return;
  enqueue({ type: "click", name: label, path: currentPath ?? undefined });
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------
export function startAnalytics() {
  if (started || !isBrowser() || isPrerender()) return;
  started = true;

  document.addEventListener("click", onClick, { capture: true, passive: true });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      sendEngagement();
      pageEnterTs = Date.now(); // reset so we don't double-count after return
      flush(true);
    }
  });

  window.addEventListener("pagehide", () => {
    sendEngagement();
    flush(true);
  });
}
